/**
 * InfinitSales - SSE Context
 * React Context for Server-Sent Events connection management
 * Provides real-time updates throughout the application
 */
import React, { 
  createContext, 
  useContext, 
  useEffect, 
  useState,
  useCallback,
  useMemo,
  useRef
} from 'react';
import sseService, { SSE_EVENTS, SSE_STATES } from '../services/global/sseService';
import { useAuth } from './AuthContext';

// Create context
const SSEContext = createContext(null);

/**
 * SSE Provider Component
 */
export const SSEProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  
  // Connection state
  const [sseState, setSSEState] = useState(SSE_STATES.DISCONNECTED);
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectInfo, setReconnectInfo] = useState(null);
  
  // Last events (for components that mount after events).
  // NEW_MESSAGE nao vira estado do provider: guardar a ultima mensagem aqui
  // re-renderizava todos os consumidores do contexto a cada mensagem recebida.
  // Quem precisa de NEW_MESSAGE assina pelo useSSEEvent / subscribe.
  const [lastConnectionUpdate, setLastConnectionUpdate] = useState(null);

  /**
   * Connect to SSE when authenticated
   */
  useEffect(() => {
    if (isAuthenticated && token) {
      console.log('[SSEContext] Authenticated, connecting SSE...');
      sseService.connect(token);
    } else {
      console.log('[SSEContext] Not authenticated, disconnecting SSE...');
      sseService.disconnect();
    }

    return () => {
      // Cleanup on unmount
      sseService.disconnect();
    };
  }, [isAuthenticated, token]);

  /**
   * Mobile: o SO congela a aba em background e mata o EventSource; o backoff
   * desiste (FAILED terminal) e o realtime não voltava até re-login. Ao
   * VOLTAR para o app, garante a conexão viva na hora.
   */
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        sseService.ensureAlive();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isAuthenticated]);

  /**
   * Subscribe to SSE state changes
   */
  useEffect(() => {
    const handleStateChange = ({ state, attempt, maxAttempts }) => {
      setSSEState(state);
      setIsConnected(state === SSE_STATES.CONNECTED);
      
      if (state === SSE_STATES.RECONNECTING) {
        setReconnectInfo({ attempt, maxAttempts });
      } else {
        setReconnectInfo(null);
      }
    };

    const unsubscribe = sseService.on('stateChange', handleStateChange);
    return unsubscribe;
  }, []);

  /**
   * Subscribe to CONNECTION_UPDATE events
   */
  useEffect(() => {
    const handleConnectionUpdate = (data) => {
      console.log('[SSEContext] Connection update:', data);
      setLastConnectionUpdate({
        ...data,
        receivedAt: new Date().toISOString()
      });
    };

    const unsubscribe = sseService.on(SSE_EVENTS.CONNECTION_UPDATE, handleConnectionUpdate);
    return unsubscribe;
  }, []);

  /**
   * Subscribe to a SSE event
   * Returns unsubscribe function
   */
  const subscribe = useCallback((event, callback) => {
    return sseService.on(event, callback);
  }, []);

  /**
   * Unsubscribe from a SSE event
   */
  const unsubscribe = useCallback((event, callback) => {
    sseService.off(event, callback);
  }, []);

  /**
   * Manually reconnect
   */
  const reconnect = useCallback(() => {
    sseService.reconnect();
  }, []);

  /**
   * Context value (memoizado: o provider so propaga re-render quando o
   * estado da conexao muda; subscribe/unsubscribe/reconnect sao estaveis).
   */
  const value = useMemo(() => ({
    // State
    sseState,
    isConnected,
    reconnectInfo,

    // Last events
    lastConnectionUpdate,

    // Methods
    subscribe,
    unsubscribe,
    reconnect,

    // Event types for convenience
    SSE_EVENTS,
    SSE_STATES,
  }), [sseState, isConnected, reconnectInfo, lastConnectionUpdate, subscribe, unsubscribe, reconnect]);

  return (
    <SSEContext.Provider value={value}>
      {children}
    </SSEContext.Provider>
  );
};

/**
 * Hook to use SSE context
 */
export const useSSE = () => {
  const context = useContext(SSEContext);
  
  if (!context) {
    throw new Error('useSSE must be used within a SSEProvider');
  }
  
  return context;
};

/**
 * Hook to subscribe to a specific SSE event
 * Automatically handles subscribe/unsubscribe on mount/unmount
 * 
 * @param {string} event - Event name from SSE_EVENTS
 * @param {function} callback - Callback function
 * @param {array} deps - Dependencies array (like useEffect)
 */
export const useSSEEvent = (event, callback, deps = []) => {
  const { subscribe } = useSSE();
  const callbackRef = useRef(callback);
  
  // Update callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);
  
  useEffect(() => {
    const handler = (data, timestamp) => {
      callbackRef.current(data, timestamp);
    };
    
    const unsubscribe = subscribe(event, handler);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, subscribe, ...deps]);
};

/**
 * Hook for WhatsApp connection status via SSE
 */
export const useWhatsAppConnection = () => {
  const { lastConnectionUpdate, isConnected: sseConnected } = useSSE();
  
  const [connectionStatus, setConnectionStatus] = useState({
    status: 'checking',
    connected: false,
    phoneNumber: null,
  });

  // Update from SSE events
  useEffect(() => {
    if (lastConnectionUpdate) {
      setConnectionStatus({
        status: lastConnectionUpdate.status,
        connected: lastConnectionUpdate.connected,
        phoneNumber: lastConnectionUpdate.phone_number || lastConnectionUpdate.phoneNumber,
      });
    }
  }, [lastConnectionUpdate]);

  return {
    ...connectionStatus,
    sseConnected,
  };
};

/**
 * Hook for new messages via SSE
 * 
 * @param {string} remoteJid - Optional filter for specific chat
 */
export const useNewMessages = (remoteJid = null) => {
  const { subscribe, SSE_EVENTS } = useSSE();
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    const handleNewMessage = (data) => {
      // Filter by remoteJid if provided
      if (remoteJid && data.remoteJid !== remoteJid) {
        return;
      }
      
      setMessages(prev => [...prev, data]);
    };

    const unsubscribe = subscribe(SSE_EVENTS.NEW_MESSAGE, handleNewMessage);
    return unsubscribe;
  }, [subscribe, SSE_EVENTS, remoteJid]);

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return { messages, clearMessages };
};

/**
 * Hook for message status updates via SSE
 */
export const useMessageStatus = () => {
  const { subscribe, SSE_EVENTS } = useSSE();
  const [statusUpdates, setStatusUpdates] = useState(new Map());

  useEffect(() => {
    const handleStatusUpdate = (data) => {
      setStatusUpdates(prev => {
        const next = new Map(prev);
        next.set(data.messageId, {
          status: data.status,
          statusCode: data.statusCode,
          updatedAt: new Date().toISOString(),
        });
        return next;
      });
    };

    const unsubscribe = subscribe(SSE_EVENTS.MESSAGE_STATUS, handleStatusUpdate);
    return unsubscribe;
  }, [subscribe, SSE_EVENTS]);

  // Get status for a specific message
  const getStatus = useCallback((messageId) => {
    return statusUpdates.get(messageId);
  }, [statusUpdates]);

  return { statusUpdates, getStatus };
};

/**
 * Hook for typing/presence indicators
 */
export const usePresence = (remoteJid = null) => {
  const { subscribe, SSE_EVENTS } = useSSE();
  const [presences, setPresences] = useState(new Map());

  useEffect(() => {
    const handlePresenceUpdate = (data) => {
      if (remoteJid && data.remoteJid !== remoteJid) {
        return;
      }

      setPresences(prev => {
        const next = new Map(prev);
        
        if (data.presences) {
          Object.entries(data.presences).forEach(([jid, presence]) => {
            next.set(jid, {
              ...presence,
              updatedAt: new Date().toISOString(),
            });
          });
        }
        
        return next;
      });
    };

    const unsubscribe = subscribe(SSE_EVENTS.PRESENCE_UPDATE, handlePresenceUpdate);
    return unsubscribe;
  }, [subscribe, SSE_EVENTS, remoteJid]);

  // Check if someone is typing
  const isTyping = useCallback((jid) => {
    const presence = presences.get(jid);
    return presence?.lastKnownPresence === 'composing';
  }, [presences]);

  return { presences, isTyping };
};

// Export SSE_EVENTS and SSE_STATES for convenience
export { SSE_EVENTS, SSE_STATES };

export default SSEContext;